// WS5 PR5 — Ride state-machine (reducer puro).
//
// Cfr. types.ts per RideState/Event. Pattern reducer puro speculare di
// `walk/state-machine.ts` e `viewbox/state-machine.ts`: ritorna stesso
// reference su no-op per consentire dispatch a short-circuit equality.
//
// Mutex con altre state-machine (camera ownership) e' gestito a livello
// orchestrator (chi chiama dispatchRideEvent), NON nel reducer. Il reducer
// applica solo invarianti locali: phase transition validity + integer/finite
// guard sui campi numerici malformed.

import {
  INITIAL_RIDE_STATE,
  RIDE_COOLDOWN_POST_MS,
  RIDE_MAX_DURATION_MS,
  RIDE_MIN_DURATION_MS,
  type RideEvent,
  type RideSnapshot,
  type RideState,
} from './types.js';

function isFiniteVec3(v: readonly [number, number, number]): boolean {
  return Number.isFinite(v[0]) && Number.isFinite(v[1]) && Number.isFinite(v[2]);
}

function isValidStartPayload(
  e: Extract<RideEvent, { type: 'START_TO_VIEWBOX' | 'START_TO_HOTSPOT' }>,
): boolean {
  if (!isFiniteVec3(e.startPos)) return false;
  if (!isFiniteVec3(e.targetPos)) return false;
  if (!isFiniteVec3(e.targetAnchor)) return false;
  if (!Number.isFinite(e.startYaw)) return false;
  if (!Number.isFinite(e.startPitch)) return false;
  if (!Number.isFinite(e.targetYaw)) return false;
  if (!Number.isFinite(e.targetPitch)) return false;
  if (!Number.isFinite(e.durationMs)) return false;
  if (!Number.isFinite(e.startTimeMs)) return false;
  if (e.durationMs < RIDE_MIN_DURATION_MS - 1) return false;
  if (e.durationMs > RIDE_MAX_DURATION_MS + 1) return false;
  return true;
}

export function rideTransition(state: RideState, event: RideEvent): RideState {
  switch (event.type) {
    case 'START_TO_VIEWBOX': {
      // Solo da idle. Tentare di iniziare ride durante ride_to_* attiva
      // → no-op (caller deve aspettare COMPLETE o forzare ABORT).
      if (state.phase !== 'idle') return state;
      if (!Number.isInteger(event.arrayIdx) || event.arrayIdx < 0) return state;
      if (!isValidStartPayload(event)) return state;
      return {
        phase: 'ride_to_viewbox',
        startPos: event.startPos,
        startYaw: event.startYaw,
        startPitch: event.startPitch,
        targetPos: event.targetPos,
        targetAnchor: event.targetAnchor,
        targetYaw: event.targetYaw,
        targetPitch: event.targetPitch,
        durationMs: event.durationMs,
        startTimeMs: event.startTimeMs,
        targetVbArrayIdx: event.arrayIdx,
        cooldownUntilMs: state.cooldownUntilMs,
        trigger: event.trigger,
      };
    }

    case 'START_TO_HOTSPOT': {
      if (state.phase !== 'idle') return state;
      if (!Number.isInteger(event.hotspotIdx) || event.hotspotIdx < 0) return state;
      if (!isValidStartPayload(event)) return state;
      return {
        phase: 'ride_to_hotspot',
        startPos: event.startPos,
        startYaw: event.startYaw,
        startPitch: event.startPitch,
        targetPos: event.targetPos,
        targetAnchor: event.targetAnchor,
        targetYaw: event.targetYaw,
        targetPitch: event.targetPitch,
        durationMs: event.durationMs,
        startTimeMs: event.startTimeMs,
        targetVbArrayIdx: -1,
        cooldownUntilMs: state.cooldownUntilMs,
        trigger: 'hotspot_click',
      };
    }

    case 'COMPLETE': {
      // Solo da ride_to_*. Ritorna idle + arma cooldown.
      if (state.phase === 'idle') return state;
      if (!Number.isFinite(event.nowMs)) return state;
      return {
        ...INITIAL_RIDE_STATE,
        cooldownUntilMs: event.nowMs + RIDE_COOLDOWN_POST_MS,
      };
    }

    case 'ABORT': {
      // Idempotente (idle → idle == same ref). Cooldown attivato anche su
      // ABORT per evitare re-trigger immediato se XR/cinematic ha ABORTato
      // mentre l'utente era contro l'edge.
      if (state.phase === 'idle') return state;
      if (!Number.isFinite(event.nowMs)) return state;
      return {
        ...INITIAL_RIDE_STATE,
        cooldownUntilMs: event.nowMs + RIDE_COOLDOWN_POST_MS,
      };
    }

    case 'RESET': {
      // Hard reset su model unload/dispose. Azzera anche cooldown.
      return INITIAL_RIDE_STATE;
    }
  }
}

export function rideSnapshot(state: RideState, nowMs: number): RideSnapshot {
  const elapsed = state.phase === 'idle' ? 0 : Math.max(0, nowMs - state.startTimeMs);
  return {
    phase: state.phase,
    durationMs: state.durationMs,
    elapsedMs: elapsed,
    trigger: state.trigger,
    targetVbArrayIdx: state.targetVbArrayIdx,
  };
}

/** Helper: il ride e' completato (now-start >= duration)? Pure, usato dal
 *  rideTick orchestrator per emettere COMPLETE event al termine. */
export function isRideDone(state: RideState, nowMs: number): boolean {
  if (state.phase === 'idle') return false;
  if (!Number.isFinite(nowMs)) return false;
  return nowMs - state.startTimeMs >= state.durationMs;
}

/** Helper: il cooldown post-ride e' ancora attivo? Usato dal edge trigger
 *  per skippare auto-ride immediato post-COMPLETE. */
export function isInCooldown(state: RideState, nowMs: number): boolean {
  if (!Number.isFinite(nowMs)) return false;
  return nowMs < state.cooldownUntilMs;
}
